// Đợt Offline-3 (06/2026) — Offline Main Routes Pack: IndexedDB snapshot store.
//
// CHỈ persist policy đánh dấu tường minh trong PERSISTABLE_POLICIES —
// tất cả là dữ liệu PUBLIC-ANONYMOUS (edgeGet dùng axios `plain` không
// cookie/auth). TUYỆT ĐỐI không thêm policy chứa dữ liệu user đăng nhập
// (chat, token, signed URL, publisher state, elo_ranking personalized...).
//
// Giới hạn: LRU ~30 record, mỗi record ≤ 400KB, debounce ghi 60s/key.
// Metadata: savedAt (thời điểm LƯU — tách bạch với dataUpdatedAt của RQ).

const PERSISTABLE_POLICIES = new Set([
  "t_list",          // danh sách giải (trang chủ + /athlete/tournaments)
  "t_detail",        // core tournament (public-anonymous)
  "t_snapshot",      // composite trang giải (6 fragment public)
  "elo_ranking_list",// BXH ELO bản anonymous (is_me FE tự tính)
  "quick_ranking",   // quick ranking trang chủ
  "branding",        // branding public
  "vn_provinces",    // bảng tỉnh (filter BXH)
]);

const DB_NAME = "pl_offline_v1";
const STORE = "snap";
const MAX_RECORDS = 30;
const MAX_BYTES_PER_RECORD = 400_000;
const WRITE_GAP_MS = 60_000;

let _dbPromise = null;
const _lastWriteAt = new Map(); // key -> ts (debounce ghi)

export function isPersistable(policyKey) {
  return PERSISTABLE_POLICIES.has(policyKey);
}

function _open() {
  if (_dbPromise) return _dbPromise;
  _dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("indexedDB unavailable"));
      return;
    }
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const st = db.createObjectStore(STORE, { keyPath: "key" });
        st.createIndex("savedAt", "savedAt");
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
    req.onblocked = () => reject(new Error("indexedDB blocked"));
  }).catch((e) => {
    _dbPromise = null;
    throw e;
  });
  return _dbPromise;
}

function _tx(db, mode) {
  return db.transaction(STORE, mode).objectStore(STORE);
}

function _reqAsPromise(req) {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function _trimLru(db) {
  const st = _tx(db, "readwrite");
  const keys = await _reqAsPromise(st.getAllKeys());
  if (keys.length <= MAX_RECORDS) return;
  const excess = keys.length - MAX_RECORDS;
  await new Promise((resolve) => {
    let deleted = 0;
    const cur = _tx(db, "readwrite").index("savedAt").openCursor();
    cur.onsuccess = () => {
      const c = cur.result;
      if (!c || deleted >= excess) { resolve(); return; }
      c.delete();
      deleted += 1;
      c.continue();
    };
    cur.onerror = () => resolve();
  });
}

// Trả true nếu đã ghi thật (dùng cho telemetry). Fire-and-forget an toàn.
export async function persistSnapshot(key, policyKey, res) {
  if (!isPersistable(policyKey)) return false;
  const now = Date.now();
  const last = _lastWriteAt.get(key) || 0;
  if (now - last < WRITE_GAP_MS) return false;
  let payload;
  try {
    payload = JSON.stringify(res?.data);
  } catch {
    return false;
  }
  if (!payload || payload.length > MAX_BYTES_PER_RECORD) return false;
  _lastWriteAt.set(key, now);
  if (_lastWriteAt.size > 200) {
    _lastWriteAt.delete(_lastWriteAt.keys().next().value);
  }
  const db = await _open();
  await _reqAsPromise(_tx(db, "readwrite").put({
    key,
    policyKey,
    data: JSON.parse(payload),
    status: res?.status || 200,
    savedAt: now,
    source: "snapshot",
  }));
  await _trimLru(db);
  return true;
}

// null nếu không có. Record: { data, status, savedAt, policyKey }.
export async function readSnapshot(key) {
  const db = await _open();
  const rec = await _reqAsPromise(_tx(db, "readonly").get(key));
  return rec || null;
}

export async function listSnapshotKeys() {
  const db = await _open();
  return _reqAsPromise(_tx(db, "readonly").getAllKeys());
}

// Chỉ dùng cho unit test.
export async function _resetSnapshotsForTest() {
  _lastWriteAt.clear();
  try {
    const db = await _open();
    await _reqAsPromise(_tx(db, "readwrite").clear());
  } catch { /* noop */ }
}
